import { useState, useEffect, useRef, forwardRef } from "react";
import { useLocation } from "wouter";
import { Link } from "@/components/SmartLink";
import { MoreHorizontal, FileText, Sparkles, Brain, Rocket, LayoutGrid, Globe, Heart, Lightbulb, Shield, Briefcase, Leaf, Users, Cpu, Scale, TrendingUp, Zap, Building, Building2, HeartPulse, BookOpen, GraduationCap, Landmark, HandCoins, Microscope, Network, Binary, Video } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { sections, getActiveSection, getSubNavItems, getLevel3Items, isPathActive, getMembershipNavItems, MAX_VISIBLE_NAV_ITEMS, type SubNavItem, type Section } from "@/lib/navigationConfig";
import { useAuth } from "@/hooks/useAuth";
import { isCapitalDomain } from "@/lib/fundLinks";
import { useMediaCategories, type MediaCategory } from "@/hooks/useMediaCategories";
import { useQuery } from "@tanstack/react-query";
import type { LucideIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const iconMap: Record<string, LucideIcon> = {
  Brain, Rocket, LayoutGrid, Globe, Heart, Lightbulb, Shield, Briefcase,
  Leaf, Users, Cpu, Scale, TrendingUp, Zap, Building, Building2, HeartPulse,
  BookOpen, Sparkles, FileText, GraduationCap, Landmark, HandCoins, Microscope, Network, Binary,
};

function resolveIcon(iconName?: string | null): LucideIcon | undefined {
  if (!iconName) return undefined;
  return iconMap[iconName];
}

function buildDynamicCategoryItems(
  basePath: string,
  userFollows: string[],
  popularCategories: MediaCategory[],
  allCategories: MediaCategory[],
  baseIcon: LucideIcon,
): SubNavItem[] {
  const firstLabel = basePath === "/media/topics" ? "All Topics" : "All";
  const items: SubNavItem[] = [
    { label: firstLabel, href: basePath, icon: baseIcon },
  ];

  const seen = new Set<string>();
  const maxItems = 6;

  const categoryBySlug = new Map(allCategories.map(c => [c.slug, c]));

  for (const slug of userFollows) {
    if (seen.size >= maxItems) break;
    const cat = categoryBySlug.get(slug);
    if (cat && !seen.has(slug)) {
      seen.add(slug);
      items.push({
        label: cat.name,
        href: `/media/topics/${slug}`,
        icon: resolveIcon(cat.icon),
      });
    }
  }

  for (const cat of popularCategories) {
    if (seen.size >= maxItems) break;
    if (!seen.has(cat.slug)) {
      seen.add(cat.slug);
      items.push({
        label: cat.name,
        href: `/media/topics/${cat.slug}`,
        icon: resolveIcon(cat.icon),
      });
    }
  }

  return items;
}

function useHashLinkHandler(href: string) {
  const [location, setLocation] = useLocation();
  const hashIndex = href.indexOf("#");
  const isHashLink = hashIndex !== -1;
  const basePath = isHashLink ? href.slice(0, hashIndex) : href;
  const hash = isHashLink ? href.slice(hashIndex + 1) : "";
  const currentPath = location.split("?")[0].split("#")[0];
  const isCurrentPage = isHashLink && (currentPath === basePath || currentPath === basePath + "/");

  const handleClick = (e: React.MouseEvent) => {
    if (isHashLink) {
      e.preventDefault();
      if (!isCurrentPage) {
        setLocation(basePath);
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      } else {
        const el = document.getElementById(hash);
        if (el) {
          const offset = 120;
          const top = el.getBoundingClientRect().top + window.pageYOffset - offset;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }
      window.history.replaceState(null, "", href);
    }
  };

  return { isHashLink, basePath, handleClick };
}

function useScrollDirection() {
  const [visible, setVisible] = useState(true);
  const lastScrollY = useRef(typeof window !== "undefined" ? window.scrollY : 0);
  const rafId = useRef<number>(0);

  useEffect(() => {
    lastScrollY.current = window.scrollY;

    const onScroll = () => {
      if (rafId.current) return;
      rafId.current = requestAnimationFrame(() => {
        const currentScrollY = window.scrollY;
        if (currentScrollY < 100) {
          setVisible(true);
        } else if (currentScrollY < lastScrollY.current) {
          setVisible(true);
        } else if (currentScrollY > lastScrollY.current + 10) {
          setVisible(false);
        }
        lastScrollY.current = currentScrollY;
        rafId.current = 0;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, []);

  return visible;
}

const OverflowNavItem = forwardRef<HTMLAnchorElement, { item: SubNavItem; active: boolean }>(
  ({ item, active, ...props }, ref) => {
    const { isHashLink, basePath, handleClick } = useHashLinkHandler(item.href);

    return (
      <Link
        ref={ref}
        href={isHashLink ? basePath : item.href}
        onClick={handleClick}
        className={cn("flex items-center gap-1.5 text-sm", active && "font-medium")}
        data-testid={`subnav-more-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        {...props}
      >
        {item.icon && <item.icon className="h-4 w-4 shrink-0" />}
        {item.label}
        {item.isNew && (
          <Badge variant="default" className="ml-auto px-1 py-0 text-[8px]">
            NEW
          </Badge>
        )}
      </Link>
    );
  }
);

function NavItem({ item, active }: { item: SubNavItem; active: boolean }) {
  const { isHashLink, basePath, handleClick } = useHashLinkHandler(item.href);

  return (
    <Link
      href={isHashLink ? basePath : item.href}
      onClick={handleClick}
      className={cn(
        "inline-flex items-center whitespace-nowrap gap-1.5 h-9 px-3 text-sm transition-colors border-b-2",
        active
          ? "text-foreground font-medium border-primary"
          : "text-muted-foreground hover:text-foreground border-transparent"
      )}
      data-testid={`subnav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {item.label}
      {item.isNew && (
        <Badge variant="default" className="ml-0.5 px-1 py-0 text-[8px]">
          NEW
        </Badge>
      )}
      {item.badge && (
        <Badge variant="outline" className="ml-0.5 px-1 py-0 text-[8px]">
          {item.badge}
        </Badge>
      )}
    </Link>
  );
}

interface SectionSubNavProps {
  navDepth?: 0 | 1 | 2;
  activeSubItem?: SubNavItem | null;
}

export function SectionSubNav({ navDepth = 1, activeSubItem }: SectionSubNavProps) {
  const [location] = useLocation();
  const { user, activeRoles } = useAuth();
  const isVisible = useScrollDirection();

  const { data: userInterests } = useQuery<{ slug: string }[]>({
    queryKey: ["/api/user-interests"],
    staleTime: 10 * 60 * 1000,
    enabled: !!user,
  });
  const userInterestSlugs = userInterests?.map(i => i.slug) || [];
  
  const activeSection = getActiveSection(location);
  
  const isDynamicMediaPath = activeSection === "discover" && navDepth === 2 &&
    (activeSubItem?.href === "/media/articles" || activeSubItem?.href === "/media" || activeSubItem?.href === "/media/topics" || activeSubItem?.href === "/videos");

  const { categories: allCategories } = useMediaCategories();

  const { data: popularCategories } = useQuery<MediaCategory[]>({
    queryKey: ["/api/media-categories/popular"],
    queryFn: async () => {
      const res = await fetch("/api/media-categories/popular?limit=8");
      if (!res.ok) throw new Error("Failed to fetch popular categories");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    enabled: isDynamicMediaPath,
  });

  const { data: followedCategories } = useQuery<{ id: number; category: string }[]>({
    queryKey: ["/api/categories/following"],
    enabled: isDynamicMediaPath && !!user,
  });

  const isEventLandingPage = /^\/events\/forum\//.test(location);
  /** Fund sub-pages render their own FundSubNavigation — hide this bar to avoid double nav */
  const isFundSubPage = location.startsWith("/capital/fund") || isCapitalDomain();
  const isStartupBenefitsPage = location === "/startup-benefits" || location.startsWith("/startup-benefits/");
  if (!activeSection || navDepth === 0 || isEventLandingPage || isFundSubPage || isStartupBenefitsPage) return null;
  
  const config = sections[activeSection];

  let items: SubNavItem[];

  if (activeSection === "membership") {
    items = getMembershipNavItems(user ? { isPremium: !!user.isPremium, platformMembershipTier: user.platformMembershipTier, contributorTier: user.contributorTier, activeRoles } : null);
  } else if (isDynamicMediaPath && (popularCategories || allCategories.length > 0)) {
    const subItemHref = activeSubItem?.href || config.href;
    const userFollows = followedCategories?.map(f => f.category) || [];
    const baseIcon = subItemHref === "/media" ? Sparkles : subItemHref === "/media/topics" ? LayoutGrid : subItemHref === "/videos" ? Video : FileText;
    items = buildDynamicCategoryItems(
      subItemHref,
      userFollows,
      popularCategories || [],
      allCategories,
      baseIcon,
    );
  } else if (navDepth === 2) {
    const subItemHref = activeSubItem?.href || config.href;
    const l3Items = getLevel3Items(subItemHref);
    items = l3Items || getSubNavItems(activeSection, user?.role, activeRoles, userInterestSlugs);
  } else {
    items = getSubNavItems(activeSection, user?.role, activeRoles, userInterestSlugs);
  }

  if (items.length === 0) return null;

  const checkActive = (href: string) => {
    const hrefHash = href.indexOf("#") !== -1 ? href.slice(href.indexOf("#")) : "";
    if (hrefHash) {
      const currentHash = typeof window !== "undefined" ? window.location.hash : "";
      const hrefBase = href.slice(0, href.indexOf("#"));
      const cleanPath = location.split("?")[0].split("#")[0];
      return (cleanPath === hrefBase || cleanPath === hrefBase + "/") && currentHash === hrefHash;
    }
    const active = isPathActive(location, href);
    if (!active) return false;
    const cleanPath = location.split("?")[0].split("#")[0];
    if (cleanPath !== href && cleanPath.startsWith(href + '/')) {
      const moreSpecific = items.some(
        (other) => other.href !== href && other.href.startsWith(href + '/') && isPathActive(location, other.href)
      );
      if (moreSpecific) return false;
    }
    return true;
  };

  const visibleItems = items.slice(0, MAX_VISIBLE_NAV_ITEMS);
  const overflowItems = items.slice(MAX_VISIBLE_NAV_ITEMS);
  const hasOverflow = overflowItems.length > 0;
  const isOverflowActive = overflowItems.some((item) => checkActive(item.href));

  return (
    <div
      className={cn(
        "w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-14 z-40 transition-all duration-300 transform-gpu",
        isVisible
          ? "translate-y-0 opacity-100"
          : "-translate-y-full opacity-0 pointer-events-none"
      )}
      data-testid="section-sub-nav"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-1">
          <ScrollArea className="flex-1 w-full md:hidden">
            <nav className="flex items-center gap-1" aria-label={`${config.label} navigation`}>
              {items.map((item) => (
                <NavItem key={item.href} item={item} active={checkActive(item.href)} />
              ))}
            </nav>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>

          <nav className="hidden md:flex items-center gap-1" aria-label={`${config.label} navigation`}>
            {visibleItems.map((item) => (
              <NavItem key={item.href} item={item} active={checkActive(item.href)} />
            ))}
            {hasOverflow && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className={cn(
                      "inline-flex items-center whitespace-nowrap gap-1.5 h-9 px-3 text-sm transition-colors border-b-2",
                      isOverflowActive
                        ? "text-foreground font-medium border-primary"
                        : "text-muted-foreground hover:text-foreground border-transparent"
                    )}
                    data-testid="subnav-more"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                    <span>More</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  {overflowItems.map((item) => {
                    const active = checkActive(item.href);
                    return (
                      <DropdownMenuItem key={item.href} asChild>
                        <OverflowNavItem item={item} active={active} />
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </nav>
        </div>
      </div>
    </div>
  );
}
